import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output } from '@angular/core';
import { ModelOption, ThreadTokenUsage, TransformedChatMessage } from '../../interfaces/chat-component.interfaces';
import { formatModelDisplayName, formatUsd, getMessageCostBreakdown } from '../../utilities/token-usage.utils';

interface MessageCostRow {
  messageNumber: number;
  messageId?: string;
  roleLabel: string;
  timestampLabel: string;
  modelKey: string;
  modelDisplayName: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  inputCost: number | null;
  outputCost: number | null;
  totalCost: number | null;
  currency: string;
}

interface ThreadCreditEventRow {
  iteration: number;
  createdAtLabel: string;
  modelKey: string;
  modelDisplayName: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  computeMilliseconds: number;
  computeSeconds: number;
  modelCredits: number;
  computeCredits: number;
  totalCredits: number;
  modelCostUsd: number;
  computeCostUsd: number;
  totalCostUsd: number;
}

@Component({
  selector: 'app-thread-details-popover',
  templateUrl: './thread-details-popover.component.html',
  styleUrl: './thread-details-popover.component.scss'
})
export class ThreadDetailsPopoverComponent implements OnInit, OnChanges {
  @Input() showThreadDetailsPopover: boolean = false;
  @Input() currentThread: any | null = null;
  @Input() embedded: boolean = false;
  @Input() threadMessages: TransformedChatMessage[] = [];
  @Input() availableModels: ModelOption[] = [];
  @Input() isLocalDevelopment: boolean = false;
  @Input() useLocalBackend: boolean = false;
  
  @Output() popoverClosed = new EventEmitter<void>();
  @Output() renameThreadRequested = new EventEmitter<string>();
  @Output() backendPreferenceChanged = new EventEmitter<boolean>();
  @Output() deleteThreadRequested = new EventEmitter<void>();

  focusedItemIndex: number = 0;
  messageCostRows: MessageCostRow[] = [];
  threadCreditEventRows: ThreadCreditEventRow[] = [];
  isEditingName: boolean = false;
  threadNameDraft: string = '';
  private lastThreadId: string | null = null;

  ngOnInit(): void {
    this.focusedItemIndex = 0;
    this.rebuildUsageRows();
  }

  ngOnChanges(): void {
    this.rebuildUsageRows();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.showThreadDetailsPopover) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const isTextEntry = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';

    if (isTextEntry) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.cancelEditingName();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.saveThreadName();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) {
          // Shift+Down: Jump to bottom (delete button)
          this.jumpToBottom();
        } else {
          this.navigateDown();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.navigateUp();
        break;
      case 'Enter':
        event.preventDefault();
        event.stopPropagation();
        this.activateFocusedItem();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.popoverClosed.emit();
        break;
    }
  }

  navigateDown(): void {
    this.focusedItemIndex = Math.min(this.focusedItemIndex + 1, this.totalFocusableItems - 1);
    this.scrollFocusedItemIntoView();
  }

  navigateUp(): void {
    this.focusedItemIndex = Math.max(this.focusedItemIndex - 1, 0);
    this.scrollFocusedItemIntoView();
  }

  jumpToBottom(): void {
    this.focusedItemIndex = this.totalFocusableItems - 1;
    this.scrollFocusedItemIntoView();
  }

  scrollFocusedItemIntoView(): void {
    if (typeof document === 'undefined') {
      return;
    }

    setTimeout(() => {
      const focusedElement = document.querySelector('.thread-details .focusable-item.focused');
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }

  get totalFocusableItems(): number {
    let count = this.threadInfoFocusableCount + 1;
    if (this.hasUsageSummary) count += 5;
    return count;
  }

  get threadInfoFocusableCount(): number {
    return 4 + (this.currentThread?.lastUsedModel ? 1 : 0);
  }

  get backendStartIndex(): number {
    return this.threadInfoFocusableCount;
  }

  get actionStartIndex(): number {
    return this.threadInfoFocusableCount;
  }

  get tokenUsageStartIndex(): number {
    return this.actionStartIndex + 1;
  }

  onStopPropagation(event: Event): void {
    event.stopPropagation();
  }

  onBackendSelected(useLocalBackend: boolean): void {
    this.useLocalBackend = useLocalBackend;
    this.backendPreferenceChanged.emit(useLocalBackend);
  }

  onDeleteThreadSelected(): void {
    this.deleteThreadRequested.emit();
  }

  startEditingName(): void {
    this.threadNameDraft = String(this.currentThread?.name || '').trim();
    this.isEditingName = true;
  }

  cancelEditingName(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.isEditingName = false;
    this.threadNameDraft = String(this.currentThread?.name || '').trim();
  }

  saveThreadName(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const trimmedName = this.threadNameDraft.trim();
    if (!trimmedName || trimmedName === String(this.currentThread?.name || '').trim()) {
      this.cancelEditingName();
      return;
    }

    this.renameThreadRequested.emit(trimmedName);
    this.isEditingName = false;
  }

  private activateFocusedItem(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const focusedElement = document.querySelector('.thread-details .focusable-item.focused') as HTMLElement | null;
    focusedElement?.click();
  }

  // Token usage helper methods
  get hasTokenUsage(): boolean {
    return !!this.currentThread?.tokenUsage;
  }

  get hasCreditEvents(): boolean {
    return this.threadCreditEventRows.length > 0;
  }

  get hasUsageSummary(): boolean {
    return this.hasCreditEvents || this.hasTokenUsage;
  }

  get hasContextUsage(): boolean {
    return !!this.currentThread?.contextUsage;
  }

  get contextUsage(): any | undefined {
    return this.currentThread?.contextUsage;
  }

  get contextFillRatio(): number | null {
    const ratio = Number(this.contextUsage?.fillRatio);
    if (Number.isFinite(ratio)) {
      return Math.max(0, Math.min(1, ratio));
    }

    const promptTokens = Number(this.contextUsage?.promptTokens || 0);
    const contextWindowTokens = Number(this.contextUsage?.contextWindowTokens || 0);
    if (promptTokens > 0 && contextWindowTokens > 0) {
      return Math.max(0, Math.min(1, promptTokens / contextWindowTokens));
    }

    return null;
  }

  get contextFillPercent(): number | null {
    const ratio = this.contextFillRatio;
    return ratio === null ? null : Math.round(ratio * 100);
  }

  get contextFillWidth(): string {
    return `${this.contextFillPercent ?? 0}%`;
  }

  get contextUsageTone(): 'low' | 'medium' | 'high' | 'unknown' {
    const ratio = this.contextFillRatio;
    if (ratio === null) {
      return 'unknown';
    }

    if (ratio >= 0.85) {
      return 'high';
    }

    if (ratio >= 0.65) {
      return 'medium';
    }

    return 'low';
  }

  get contextUsageModelLabel(): string {
    const model = String(this.contextUsage?.model || this.currentThread?.lastUsedModel || '').trim();
    return model ? this.getModelDisplayName(model) : 'Current model';
  }

  get contextUsageSummary(): string {
    const promptTokens = Number(this.contextUsage?.promptTokens || 0);
    const windowTokens = Number(this.contextUsage?.contextWindowTokens || 0);

    if (promptTokens > 0 && windowTokens > 0) {
      return `${this.formatTokenCount(promptTokens)} / ${this.formatTokenCount(windowTokens)} tokens`;
    }

    if (promptTokens > 0) {
      return `${this.formatTokenCount(promptTokens)} prompt tokens`;
    }

    return 'Waiting for usage data';
  }

  get contextUsageSourceLabel(): string {
    return this.contextUsage?.source === 'estimate' ? 'Estimated before request' : 'From provider usage';
  }

  get tokenUsage(): ThreadTokenUsage | undefined {
    return this.currentThread?.tokenUsage;
  }

  get usageTotalCredits(): number {
    return this.threadCreditEventRows.reduce((sum, row) => sum + row.totalCredits, 0);
  }

  get usageModelCredits(): number {
    return this.threadCreditEventRows.reduce((sum, row) => sum + row.modelCredits, 0);
  }

  get usageComputeCredits(): number {
    return this.threadCreditEventRows.reduce((sum, row) => sum + row.computeCredits, 0);
  }

  get usageTotalInputTokens(): number {
    if (this.hasCreditEvents) {
      return this.threadCreditEventRows.reduce((sum, row) => sum + row.promptTokens, 0);
    }

    return Number(this.tokenUsage?.totalPromptTokens || 0);
  }

  get usageTotalOutputTokens(): number {
    if (this.hasCreditEvents) {
      return this.threadCreditEventRows.reduce((sum, row) => sum + row.completionTokens, 0);
    }

    return Number(this.tokenUsage?.totalCompletionTokens || 0);
  }

  get usageTotalTokens(): number {
    if (this.hasCreditEvents) {
      return this.threadCreditEventRows.reduce((sum, row) => sum + row.totalTokens, 0);
    }

    return Number(this.tokenUsage?.totalTokens || 0);
  }

  get usageTotalComputeSeconds(): number {
    return this.threadCreditEventRows.reduce((sum, row) => sum + row.computeSeconds, 0);
  }

  get usageTotalCostUsd(): number {
    return this.threadCreditEventRows.reduce((sum, row) => sum + row.totalCostUsd, 0);
  }

  get usageIterationsCount(): number {
    return this.threadCreditEventRows.length;
  }

  formatTokenCount(count: number): string {
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    } else if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count.toString();
  }

  formatCredits(value: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: value >= 100 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  formatUsdCost(value: number): string {
    return formatUsd(value, 'USD');
  }

  formatDuration(seconds: number, milliseconds?: number): string {
    if (milliseconds !== undefined && milliseconds > 0 && milliseconds < 1000) {
      return `${milliseconds}ms`;
    }

    if (seconds < 60) {
      const precision = seconds >= 10 ? 1 : 2;
      return `${seconds.toFixed(precision)}s`;
    }

    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`;
  }

  get hasMessageCostRows(): boolean {
    return this.messageCostRows.length > 0;
  }

  get pricedMessageCostRows(): MessageCostRow[] {
    return this.messageCostRows.filter(row => row.totalCost !== null);
  }

  get pricedMessageCount(): number {
    return this.pricedMessageCostRows.length;
  }

  get unavailableMessageCostCount(): number {
    return this.messageCostRows.filter(row => row.totalCost === null).length;
  }

  get hasCalculatedMessageCost(): boolean {
    return this.pricedMessageCount > 0;
  }

  get calculatedMessageCostTotal(): number {
    return this.pricedMessageCostRows.reduce((sum, row) => sum + (row.totalCost || 0), 0);
  }

  get calculatedMessageCostCurrency(): string {
    return this.pricedMessageCostRows[0]?.currency || 'USD';
  }

  formatCost(value: number, currency: string = 'USD'): string {
    return formatUsd(value, currency);
  }

  formatRowCost(value: number | null, currency: string = 'USD'): string {
    if (value === null) {
      return 'Unavailable';
    }

    return this.formatCost(value, currency);
  }

  get threadAge(): string {
    if (!this.currentThread?.createdAt) return '';
    
    const created = this.currentThread.createdAt.toDate ? 
      this.currentThread.createdAt.toDate() : 
      new Date(this.currentThread.createdAt);
    
    const now = new Date();
    const diff = now.getTime() - created.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return `${days}d ${hours}h ago`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ago`;
    } else {
      return `${minutes}m ago`;
    }
  }

  get statusDisplayName(): string {
    switch (this.currentThread?.status) {
      case 'continuing': return 'Continuing';
      case 'waiting_for_user': return 'Waiting for User';
      case 'complete': return 'Complete';
      case 'error': return 'Error';
      case 'running': return 'Running';
      case 'stopped': return 'Stopped';
      default: return 'Unknown';
    }
  }

  get statusIcon(): string {
    switch (this.currentThread?.status) {
      case 'continuing': return 'fas fa-play-circle';
      case 'waiting_for_user': return 'fas fa-pause-circle';
      case 'complete': return 'fas fa-check-circle';
      case 'error': return 'fas fa-exclamation-circle';
      case 'running': return 'fas fa-spinner fa-spin';
      case 'stopped': return 'fas fa-stop-circle';
      default: return 'fas fa-question-circle';
    }
  }

  get statusColor(): string {
    switch (this.currentThread?.status) {
      case 'continuing': return '#15cdff';
      case 'waiting_for_user': return '#ffa500';
      case 'complete': return '#10b981';
      case 'error': return '#ef4444';
      case 'running': return '#8c34ea';
      case 'stopped': return '#6b7280';
      default: return '#6b7280';
    }
  }

  getModelDisplayName(modelKey: string): string {
    return formatModelDisplayName(modelKey);
  }

  private rebuildUsageRows(): void {
    this.syncThreadNameDraft();
    this.rebuildMessageCostRows();
    this.rebuildThreadCreditEventRows();
  }

  private syncThreadNameDraft(): void {
    const nextThreadId = String(this.currentThread?.id || '');

    if (nextThreadId !== this.lastThreadId) {
      this.lastThreadId = nextThreadId;
      this.isEditingName = false;
      this.threadNameDraft = String(this.currentThread?.name || '').trim();
      return;
    }

    if (!this.isEditingName) {
      this.threadNameDraft = String(this.currentThread?.name || '').trim();
    }
  }

  private rebuildMessageCostRows(): void {
    const costBearingMessages = (this.threadMessages || [])
      .filter(message => !!message?.tokenUsage);

    this.messageCostRows = costBearingMessages.map((message, index) => {
      const tokenUsage = message.tokenUsage!;
      const breakdown = getMessageCostBreakdown(tokenUsage, this.availableModels);
      const messageTimestamp = (message as any)?.timestamp || (message as any)?.createdAt;

      return {
        messageNumber: index + 1,
        messageId: message.id,
        roleLabel: this.getRoleLabel(message.role),
        timestampLabel: this.formatMessageTimestamp(messageTimestamp),
        modelKey: tokenUsage.model,
        modelDisplayName: this.getModelDisplayName(tokenUsage.model),
        promptTokens: tokenUsage.promptTokens,
        completionTokens: tokenUsage.completionTokens,
        totalTokens: tokenUsage.totalTokens,
        inputCost: breakdown?.inputCost ?? null,
        outputCost: breakdown?.outputCost ?? null,
        totalCost: breakdown?.totalCost ?? null,
        currency: breakdown?.currency || tokenUsage.currency || 'USD'
      };
    });
  }

  private rebuildThreadCreditEventRows(): void {
    const creditBearingMessages = (this.threadMessages || [])
      .filter(message => !!message?.creditUsage)
      .filter(message => message.role === 'assistant' || message.role === 'model');

    this.threadCreditEventRows = creditBearingMessages.map((message, index) => {
      const tokenUsage = message.tokenUsage;
      const creditUsage = message.creditUsage!;
      const modelKey = String(tokenUsage?.model || this.currentThread?.lastUsedModel || '');
      const messageTimestamp = (message as any)?.timestamp || (message as any)?.createdAt;

      return {
        iteration: Number(creditUsage.iteration || index + 1),
        createdAtLabel: this.formatEventTimestamp(messageTimestamp),
        modelKey,
        modelDisplayName: modelKey ? this.getModelDisplayName(modelKey) : 'Unknown model',
        promptTokens: Number(tokenUsage?.promptTokens || 0),
        completionTokens: Number(tokenUsage?.completionTokens || 0),
        totalTokens: Number(tokenUsage?.totalTokens || 0),
        computeMilliseconds: Number(creditUsage.computeMilliseconds || 0),
        computeSeconds: Number(creditUsage.computeSeconds || 0),
        modelCredits: Number(creditUsage.modelCredits || 0),
        computeCredits: Number(creditUsage.computeCredits || 0),
        totalCredits: Number(creditUsage.totalCredits || 0),
        modelCostUsd: Number(creditUsage.modelCostUsd || 0),
        computeCostUsd: Number(creditUsage.computeCostUsd || 0),
        totalCostUsd: Number(creditUsage.totalCostUsd || 0)
      };
    });
  }

  private getRoleLabel(role: string): string {
    switch (role) {
      case 'assistant':
      case 'model':
        return 'Assistant';
      case 'tool':
        return 'Tool';
      case 'system':
        return 'System';
      default:
        return 'User';
    }
  }

  private formatMessageTimestamp(timestamp: any): string {
    if (!timestamp) {
      return 'Unknown time';
    }

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return 'Unknown time';
    }

    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  private formatEventTimestamp(timestamp: any): string {
    if (!timestamp) {
      return 'Unknown time';
    }

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return 'Unknown time';
    }

    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  get threadUpdated(): string {
    if (!this.currentThread?.updatedAt) return 'Never';
    
    const updated = this.currentThread.updatedAt.toDate ? 
      this.currentThread.updatedAt.toDate() : 
      new Date(this.currentThread.updatedAt);
    
    const now = new Date();
    const diff = now.getTime() - updated.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 7) {
      return updated.toLocaleDateString();
    } else if (days > 0) {
      return `${days}d ago`;
    } else if (hours > 0) {
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return 'Just now';
    }
  }
}
